import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { transactions } from '@modules/financial-planning/constants/URL'

function getQueryString(params) {
  const query = formatQueryParams(params || {})
  return query === '?' ? '' : query
}

class APIFinancialPlanningTransactions extends APITemplate {
  constructor() {
    super()

    this.reqCarregar = {}
  }

  _shouldSkipLoad(clientId, forceUpdate) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    const storedClientId =
      store.getters['financialPlanning/transactionsClientId']
    const sameClient = String(storedClientId) === String(clientId)

    if (!forceUpdate && sameClient) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/transactionsLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )

      if (!shouldUpdate) {
        return store.getters['financialPlanning/transactions']
      }
    }

    // Só devolve o estado "em carregamento" do store quando ele é do MESMO
    // cliente. Um carregamento concorrente de OUTRO cliente (ex.: o batch
    // refreshFinancialPlanning ainda em voo ao navegar via SPA até o perfil)
    // não pode fazer este pedido receber transações stale/vazias — era o que
    // deixava o card de perfil em "sem lançamentos" na primeira carga. O caso
    // do mesmo cliente com request idêntico em voo já é coberto pela dedup por
    // requestKey em carregar().
    if (sameClient && store.getters['financialPlanning/transactionsLoading']) {
      return store.getters['financialPlanning/transactions']
    }

    return null
  }

  _handleResponse(response, clientId) {
    if (response && response.status === 200) {
      const items = Array.isArray(response.value) ? response.value : []
      store.dispatch('financialPlanning/setTransactions', { items, clientId })
    } else {
      store.dispatch('financialPlanning/setTransactions', {
        items: [],
        clientId: null,
      })
    }
  }

  async _executeCarregar(clientId, requestKey, requestUrl) {
    store.dispatch('financialPlanning/setTransactionsLoading', true)
    this.reqCarregar[requestKey] = con.get(requestUrl)

    try {
      const response = await this.reqCarregar[requestKey]
      this._handleResponse(response, clientId)
      return response
    } catch (error) {
      this._handleResponse(null, clientId)
      throw error
    } finally {
      store.dispatch('financialPlanning/setTransactionsLoading', false)
      delete this.reqCarregar[requestKey]
    }
  }

  async carregar({ clientId, params = {}, forceUpdate = false } = {}) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    const query = getQueryString(params)
    const requestKey = query ? `${clientId}:${query}` : `${clientId}`

    // Request idêntico (mesmo cliente + params) já em voo: compartilha a MESMA
    // promise e devolve a resposta HTTP real, em vez de cair no guard de loading
    // do _shouldSkipLoad (que devolveria o store stale). Precede o skip de
    // propósito.
    if (this.reqCarregar[requestKey]) {
      return this.reqCarregar[requestKey]
    }

    const skipResult = this._shouldSkipLoad(clientId, forceUpdate)
    if (skipResult) return skipResult

    const requestUrl = `${transactions.list(clientId)}${query}`
    const response = await this._executeCarregar(clientId, requestKey, requestUrl)

    return response || {}
  }

  async salvar({ clientId, transaction }) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!transaction || typeof transaction !== 'object') {
      return {
        status: 400,
        message: 'Dados da transação inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(transaction),
    }

    const response = await con.post(transactions.list(clientId), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/addTransaction', {
          item: value,
          clientId,
        })
      } else {
        await this.carregar({ clientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async _syncAfterUpdate(response, clientId) {
    if (!response || response.status !== 200 || !clientId) return

    const value = Array.isArray(response.value)
      ? response.value[0]
      : response.value

    if (value && typeof value === 'object') {
      store.dispatch('financialPlanning/updateTransaction', {
        item: value,
        clientId,
      })
    } else {
      await this.carregar({ clientId, forceUpdate: true })
    }
  }

  async atualizar({ transactionId, transaction, clientId } = {}) {
    if (!transactionId) {
      return { status: 404, message: 'Transação não encontrada.' }
    }

    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    if (!transaction || typeof transaction !== 'object') {
      return { status: 400, message: 'Dados da transação inválidos.' }
    }

    const payload = { data: JSON.stringify(transaction) }

    const response = await con.put(
      transactions.detail(clientId, transactionId),
      payload
    )

    await this._syncAfterUpdate(response, clientId)

    return response || {}
  }

  async excluir({ transactionId, clientId } = {}) {
    if (!transactionId) {
      return {
        status: 404,
        message: 'Transação não encontrada.',
      }
    }

    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const response = await con.delet(
      transactions.detail(clientId, transactionId)
    )

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  async excluirEmLote({ transactionIds, clientId } = {}) {
    if (!transactionIds || !Array.isArray(transactionIds) || transactionIds.length === 0) {
      return {
        status: 400,
        message: 'Nenhuma transação selecionada para exclusão.',
      }
    }

    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const payload = {
      ids: transactionIds,
    }

    const response = await con.delet(transactions.batch(clientId), payload)

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }
}

const apiFinancialPlanningTransactions = new APIFinancialPlanningTransactions()
export { apiFinancialPlanningTransactions }
